import * as React from "react";

// Groups `sections` into category buckets, preserving each category's first-seen order and
// each section's order within it. PROMPT_HUB_SECTIONS (promptHubSections.js) already lists all
// 14 sections pre-grouped by category (Setup, Conversation, Intelligence, Knowledge & Launch),
// so this just reproduces that grouping for rendering - it doesn't re-sort anything, so a caller
// that reorders `sections` controls the nav order too.
const buildCategoryGroups = (sections) => {
  const groups = [];
  const indexByCategory = new Map();
  sections.forEach((section) => {
    if (!indexByCategory.has(section.category)) {
      indexByCategory.set(section.category, groups.length);
      groups.push({ category: section.category, sections: [] });
    }
    groups[indexByCategory.get(section.category)].sections.push(section);
  });
  return groups;
};

// Prompt Hub's left-nav + right-panel shell. Purely presentational: every piece of Prompt Hub
// state (which section is active, each section's save status, how to save it, what each
// section's fields look like) is handed in as props so this component stays reusable/testable
// without reaching into PAuthQOnBoardingForm's internals.
export const PromptHubSectionLayout = ({
  sections,
  activeSection,
  onSelectSection,
  sectionSaveState,
  sectionSaveError,
  onSave,
  renderSection,
}) => {
  const categoryGroups = React.useMemo(
    () => buildCategoryGroups(sections),
    [sections]
  );
  const activeSectionMeta = sections.find((section) => section.id === activeSection);
  // Knowledge Base keeps its own dedicated save button/handler (per-document upload, delete, and
  // a separate "Save Instructions" action) - it must not also get this generic section Save
  // button, which would save nothing for it since it has no `fields` in the registry.
  const isKnowledgeBase = activeSection === "knowledgeBase";
  const saveState = (sectionSaveState && sectionSaveState[activeSection]) || "idle";
  const saveError = (sectionSaveError && sectionSaveError[activeSection]) || "";

  return (
    <div className="prompt-hub-layout">
      <nav className="prompt-hub-nav" aria-label="Prompt Hub sections">
        {categoryGroups.map((group) => (
          <div className="prompt-hub-nav__group" key={group.category}>
            <p className="prompt-hub-nav__group-label">{group.category}</p>
            {group.sections.map((section) => {
              const isActive = section.id === activeSection;
              return (
                <button
                  type="button"
                  key={section.id}
                  className={`prompt-hub-nav__item${isActive ? " is-active" : ""}`}
                  onClick={() => onSelectSection(section.id)}
                  aria-current={isActive ? "true" : undefined}
                >
                  {section.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="prompt-hub-panel-right">
        <div className="prompt-hub-panel-right__header">
          <h4 className="prompt-hub-panel-right__title">
            {activeSectionMeta ? activeSectionMeta.label : ""}
          </h4>
        </div>

        <div className="prompt-hub-panel-right__body">
          {renderSection(activeSection)}
        </div>

        {!isKnowledgeBase && (
          <div className="prompt-hub-section-status">
            {saveState === "saving" && (
              <span className="prompt-hub-section-status__message is-saving">
                Saving&hellip;
              </span>
            )}
            {saveState === "saved" && (
              <span className="prompt-hub-section-status__message is-saved">
                Saved
              </span>
            )}
            {saveState === "error" && (
              <span className="prompt-hub-section-status__message is-error">
                {saveError || "Something went wrong. Please try again."}
              </span>
            )}
            <button
              type="button"
              className="simple-btn login-btn prompt-hub-section-status__save-btn"
              onClick={() => onSave(activeSection)}
              disabled={saveState === "saving"}
            >
              {saveState === "saving" ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                "Save"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
